import React, { useCallback, useEffect } from 'react';
import {
  Badge,
  Box,
  Flex,
  FormControl,
  Input,
  Select,
  Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { useDispatch, useSelector } from 'react-redux';
import CommonTable from 'components/commonTable/CommonTable';
import CommonPagination from 'components/commonPagination/CommonPagination';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import useListState from 'hooks/useListState';
import { formatCurrency } from 'utils/CommonFunction';
import { sideBarRoutes } from 'utils/StaticData';
import { GetPayoutLogList } from '../../../reactRedux/thunks/payoutLogThunk/payoutLogThunk';
import moment from 'moment';

const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const getStatusBadge = (status) => {
  const colorMap = {
    succeeded: 'green',
    failed: 'red',
    pending: 'yellow',
  };
  return (
    <Badge
      colorScheme={colorMap[status] || 'gray'}
      variant="solid"
      fontSize="xs"
    >
      {status || 'N/A'}
    </Badge>
  );
};

const PayoutLogList = () => {
  const dispatch = useDispatch();
  const { tabletextColor } = useColorModeVariables();

  const { params, setParam, setParams, page, setPage } = useListState({
    defaults: { limit: 10, settingId: '', status: '', sortBy: 'desc' },
  });

  const { isLoading, payoutLogList } = useSelector(
    (state) => state.PayoutLogSlice,
  );
  const { adminDetails } = useSelector((state) => state.authSlice);

  const canView =
    adminDetails?.role === sideBarRoutes?.Admin ||
    adminDetails?.role === sideBarRoutes?.Dealer;

  const getList = useCallback(() => {
    const apiParams = { page, limit: params.limit, sortBy: params.sortBy };
    if (params.settingId) apiParams.settingId = params.settingId;
    if (params.status) apiParams.status = params.status;
    dispatch(GetPayoutLogList(apiParams));
  }, [
    dispatch,
    page,
    params.limit,
    params.settingId,
    params.status,
    params.sortBy,
  ]);

  useEffect(() => {
    if (canView) {
      getList();
    }
  }, [getList, canView]);

  const columnHelper = createColumnHelper();

  const columns = [
    columnHelper.accessor((row) => row?.payoutOrder, {
      id: 'payoutOrder',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          #
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" align="left">
          {info.getValue() ?? 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.connectedAccountName, {
      id: 'connectedAccountName',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Connected Account
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue() || 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.grossRevenue, {
      id: 'grossRevenue',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Gross Revenue
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" align="left">
          {info.getValue() !== null && info.getValue() !== undefined
            ? formatCurrency(
                info.getValue(),
                info.row.original.currency ?? 'EUR',
              )
            : 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.transactionFee, {
      id: 'transactionFee',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Transaction Fee
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" align="left">
          {info.getValue() !== null && info.getValue() !== undefined
            ? formatCurrency(
                info.getValue(),
                info.row.original.currency ?? 'EUR',
              )
            : 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.deductionAmount, {
      id: 'deductionAmount',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Deduction
        </Text>
      ),
      cell: (info) => (
        <Text color={tabletextColor} fontSize="sm" align="left">
          {info.getValue() !== null && info.getValue() !== undefined
            ? formatCurrency(
                info.getValue(),
                info.row.original.currency ?? 'EUR',
              )
            : 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.payoutAmount, {
      id: 'payoutAmount',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Payout Amount
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
        >
          {info.getValue() !== null && info.getValue() !== undefined
            ? formatCurrency(
                info.getValue(),
                info.row.original.currency ?? 'EUR',
              )
            : 'N/A'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.status, {
      id: 'status',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Status
        </Text>
      ),
      cell: (info) => getStatusBadge(info.getValue()),
    }),

    columnHelper.accessor((row) => row?.stripeTransferId, {
      id: 'stripeTransferId',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Transfer ID
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="xs"
          align="left"
          whiteSpace="nowrap"
          fontFamily="monospace"
        >
          {info.getValue() || '—'}
        </Text>
      ),
    }),

    columnHelper.accessor((row) => row?.payoutDate, {
      id: 'payoutDate',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Payout Date
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue()
            ? moment(info.getValue()).format('DD-MM-YYYY HH:mm')
            : 'N/A'}
        </Text>
      ),
    }),
  ];

  const listData = payoutLogList?.list || [];
  const totalCount = payoutLogList?.total || 0;

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      {/* Filters */}
      <Flex gap={3} mb={4} flexWrap="wrap">
        <FormControl maxW="220px">
          <Input
            placeholder="Filter by Setting ID"
            size="sm"
            borderRadius="8px"
            value={params.settingId}
            onChange={(e) => {
              setParam('settingId', e.target.value);
              setPage(1);
            }}
          />
        </FormControl>

        <FormControl maxW="180px">
          <Select
            size="sm"
            borderRadius="8px"
            value={params.status}
            onChange={(e) => {
              setParam('status', e.target.value);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl maxW="160px">
          <Select
            size="sm"
            borderRadius="8px"
            value={params.sortBy}
            onChange={(e) => {
              setParam('sortBy', e.target.value);
              setPage(1);
            }}
          >
            <option value="desc">Newest First</option>
            <option value="asc">Oldest First</option>
          </Select>
        </FormControl>
      </Flex>

      <CommonTable
        columns={columns}
        data={listData}
        isLoading={isLoading}
        keyValue="id"
        isLink={false}
      />

      {totalCount > params.limit && (
        <CommonPagination
          totalCount={totalCount}
          activePage={page}
          pageCount={Math.ceil(totalCount / params.limit)}
          onPageChange={setPage}
          itemsPerPage={params.limit}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      )}
    </Box>
  );
};

export default PayoutLogList;
